






































import {
  commitRecordPracticeSet,
  commitSnackbarText,
  dispatchRestoreState,
  dispatchSaveState,
  PracticeSet,
  readRecordedPracticeSets,
  readSelectedCharacters,
  readSpacedRepetitionItems,
  RootStore,
} from "@/store";
import { compareItem, createItem } from "@/tech/SpacedRepetition";
import { assert, deepEqual, entries } from "@/utils";
import Vue from "vue";
import Component from "vue-class-component";
import { Store } from "vuex";
import CharacterSelector from "../components/training/CharacterSelector.vue";
import TrainingHeader from "../components/training/TrainingHeader.vue";
import TrainingPanel from "../components/training/TrainingPanel.vue";
import {
  CharacterId,
  GameAndCharacterId,
  GameId,
} from "../tech/AllCharacterMetadata";
import allTechMetadata, {
  serializeTechVariant,
  TechId,
} from "../tech/AllTechMetadata";
import {
  AllTechVariants,
  TechMetadata,
  TechVariant,
  variantValues,
} from "../tech/TechMetadata";
import {
  generateAllVariantCombinations,
  isSatisfactoryPracticeSet,
  isTechAvailable,
  SatisfactoryTech,
} from "../tech/TechTraining";

interface PanelData {
  techId: TechId;
  variant: TechVariant;
  numSets: number;
}

interface PanelInfo {
  panelData: PanelData[];
  firstTime: boolean;
}

type Panels = "loading" | "no-character-selected" | PanelInfo;

const MAX_NUM_EXERCISES: number = 8;

function createPanelsForTech(
  satisfactoryTech: SatisfactoryTech,
  techId: TechId,
  techMetadata: TechMetadata,
): PanelData[] {
  return generateAllVariantCombinations(
    entries(techMetadata.variants)
      .filter(x => x[1])
      .map(x => x[0]),
  )
    .filter(variant => {
      const excludeVariants = techMetadata.excludeVariants;
      if (excludeVariants === undefined) {
        return true;
      } else {
        return !excludeVariants.some(excludeVariant =>
          deepEqual(variant, excludeVariant),
        );
      }
    })
    .filter(variant => isTechAvailable(satisfactoryTech, techId, variant))
    .map(variant => ({
      techId,
      variant,
      numSets: 3,
    }));
}

function getSatisfactoryTech<T extends GameId>(
  store: RootStore,
  gameAndCharacterId: GameAndCharacterId<T>,
): SatisfactoryTech {
  const { gameId, characterId } = gameAndCharacterId;
  const satisfactoryTech: SatisfactoryTech = {};
  const recordedPracticeSets = readRecordedPracticeSets(store) || {};
  const gamePracticeSets = (recordedPracticeSets[gameId] || {}) as {
    [c in CharacterId<typeof gameId>]: Array<PracticeSet<TechId>>;
  };
  const characterPracticeSets = gamePracticeSets[characterId] || [];
  for (const practiceSet of characterPracticeSets) {
    const { techId, techVariant } = practiceSet;
    if (isSatisfactoryPracticeSet(practiceSet)) {
      const serializedTechVariant = serializeTechVariant(techId, techVariant);
      satisfactoryTech[serializedTechVariant] = true;
    }
  }
  return satisfactoryTech;
}

function createPanels<T extends GameId>(
  store: RootStore,
  gameAndCharacterId: GameAndCharacterId<T>,
): PanelInfo {
  const { gameId, characterId } = gameAndCharacterId;
  const allSpacedRepetitionItems = readSpacedRepetitionItems(store);
  const gameSpacedRepetitionItems = allSpacedRepetitionItems[gameId] || {};
  const characterSpacedRepetitionItems =
    gameSpacedRepetitionItems[characterId] || new Map();
  const firstTime = characterSpacedRepetitionItems.size === 0;
  const satisfactoryTech = getSatisfactoryTech(store, gameAndCharacterId);
  const panelData = entries(allTechMetadata)
    .flatMap(entry => {
      const [techId, techMetadata] = entry;
      return createPanelsForTech(satisfactoryTech, techId, techMetadata);
    })
    .sort((lhs, rhs) => {
      const lhsItem = characterSpacedRepetitionItems.get(
        serializeTechVariant(lhs.techId, lhs.variant),
      );
      const rhsItem = characterSpacedRepetitionItems.get(
        serializeTechVariant(rhs.techId, rhs.variant),
      );
      const result = compareItem(
        lhsItem !== undefined ? lhsItem : createItem(),
        rhsItem !== undefined ? rhsItem : createItem(),
      );
      return result;
    })
    .slice(0, MAX_NUM_EXERCISES);
  return { panelData, firstTime };
}

@Component({
  name: "training",
  components: { TrainingHeader, TrainingPanel },
})
export default class extends Vue {
  public gameId: GameId = "ssbu";
  public panels: Panels = "loading";
  public shownExpansionPanels: number[] = [0];

  public async created() {
    await dispatchRestoreState(this.$store);

    this.$watch(
      function() {
        return readSelectedCharacters(this.$store)[this.gameId];
      },
      function(newSelectedCharacter) {
        if (newSelectedCharacter === null) {
          this.panels = "no-character-selected";
        } else {
          this.resetExpansionPanels({
            gameId: this.gameId,
            characterId: newSelectedCharacter,
          });
        }
      },
      {
        immediate: true,
      },
    );
  }

  public refresh(): void {
    location.reload();
  }

  public resetExpansionPanels(
    gameAndCharacterId: GameAndCharacterId<GameId>,
  ): void {
    this.panels = createPanels(this.$store, gameAndCharacterId);
    this.shownExpansionPanels = [0];
  }

  public async onRecorded(
    i: number,
    practiceSet: PracticeSet<TechId>,
  ): Promise<void> {
    this.hidePanel(i);
    const gameId = this.gameId;
    const characterId = readSelectedCharacters(this.$store)[gameId];
    if (characterId !== null) {
      commitRecordPracticeSet(this.$store, {
        gameAndCharacterId: {
          gameId,
          characterId,
        },
        practiceSet,
      });
      await dispatchSaveState(this.$store);
      commitSnackbarText(this.$store, "Practice set recorded.");
    }
  }

  public hidePanel(i: number): void {
    this.shownExpansionPanels = this.shownExpansionPanels.filter(j => j !== i);
  }
}
